import { Banner } from '@components';
import {
  Box,
  Button,
  Grid,
  TextField,
  Typography,
  buttonBaseClasses,
  useTheme,
} from '@mui/material';
import { useFormikContext } from 'formik';
import { Refresh } from 'iconsax-react';
import uniqBy from 'lodash/uniqBy';
import { DateTime } from 'luxon';
import {
  type ReactElement,
  memo,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { getTenantUnits } from '@core/configuration';
import { getUserFacingCategoryName } from '@core/constants/product';
import type { ProjectFormikType } from '@core/formik/project';
import { usePreviousValue } from '@core/hooks/usePreviousValue';
import Enums from '@core/types/api/Enums';
import { Forecast } from '@core/types/api/Forecast/Forecast';
import { ParseServerDate } from '@core/utils/dateHelpers';
import { hasStringValue, hasValue } from '@core/utils/equality';
import {
  forecastableCategoryDisplayList,
  getDefaultUnitForCategory,
  getProductCategoriesInProject,
  sumProductQuantitiesByCategory,
} from '@core/utils/forecastDistribution';
import { getShortUnitLabel, getUnitLabel } from '@core/utils/unitHelpers';
import { SegmentedControl } from '@web/components';
import { Input } from '@web/components/Input/Input';
import { SlabTabs } from '@web/components/SlabTabs/SlabTabs';
import { YearSelector } from '@web/components/YearSelector/YearSelector';
import { distributeForecastVolume } from '@web/utils';

import { ProjectSection } from '../types';
import { RedistributeDialog } from './RedistributeDialog';

type ForecastsSectionProps = {
  initialForecastYear: number;
  onNavigateSection?: (section: ProjectSection) => void;
};

interface ForecastInputRowProps {
  formikIdx: number;
  intervalStart: string;
  manuallyEditedTracker: Record<number, boolean>;
  formikBag: any;
  disableGridWrapper?: boolean;
  /** Marks the cell as outside the project date range, which makes it non-editable. */
  isOutOfRange?: boolean;
  /** When true, the month input is rendered in a disabled (non-editable) state. */
  disabled?: boolean;
  onManualEdit: (formikIndex: number) => void;
}

interface PillSummaryProps {
  label: string;
  value: number;
  unit?: string;
}

const ForecastInputRow = memo(
  function ForecastInputRow({
    formikIdx,
    intervalStart,
    manuallyEditedTracker,
    formikBag,
    disableGridWrapper = false,
    isOutOfRange = false,
    disabled = false,
    onManualEdit,
  }: ForecastInputRowProps): ReactElement {
    const handleInputChange = useCallback(() => {
      onManualEdit(formikIdx);
      manuallyEditedTracker[formikIdx] = true;
    }, [formikIdx, manuallyEditedTracker, onManualEdit]);

    const forecast = formikBag.values.forecasts[formikIdx];
    const unitLabel =
      forecast?.kind === 'capacity' ? ` ${getUnitLabel(getTenantUnits().dryVolume)}` : '';
    const label = `${ParseServerDate(intervalStart).monthLong ?? ''}${unitLabel}`;

    const content = (
      <Input
        name={`forecasts.${formikIdx}.value`}
        label={label}
        type='number'
        allowNegative={false}
        disabled={disabled || isOutOfRange}
        onInputChange={handleInputChange}
        omitEmptyHelperText
      />
    );

    if (disableGridWrapper) {
      return content;
    }

    return (
      <Grid item xs={6}>
        {content}
      </Grid>
    );
  },
  (prevProps, nextProps) => {
    if (prevProps.formikIdx !== nextProps.formikIdx) return false;
    if (prevProps.intervalStart !== nextProps.intervalStart) return false;
    if (prevProps.disableGridWrapper !== nextProps.disableGridWrapper) return false;
    if (prevProps.isOutOfRange !== nextProps.isOutOfRange) return false;
    if (prevProps.disabled !== nextProps.disabled) return false;
    return true;
  },
);

// PillSummary: read-only summary field styled as a disabled input
const PillSummary = ({ label, value, unit }: PillSummaryProps): ReactElement => {
  const formattedValue = value.toLocaleString('en-US');
  const displayValue = unit ? `${formattedValue} ${unit}` : formattedValue;

  return (
    <TextField
      label={label}
      value={displayValue}
      fullWidth
      disabled
      sx={{ pointerEvents: 'none' }}
    />
  );
};

const EST_VOLUME_OVERRIDE_LABEL = 'Est. Volume (from Volume Override)';
const TOTAL_QUANTITY_LABEL = 'Total Quantity (from Product Quantities)';

// Helper function to validate project dates
const isNonEmptyString = (value: unknown): value is string => {
  return value !== null && value !== undefined && typeof value === 'string' && value.trim() !== '';
};

export const ForecastsSection = ({
  initialForecastYear,
  onNavigateSection,
}: ForecastsSectionProps): ReactElement => {
  const formikBag = useFormikContext<ProjectFormikType>();
  const [selectedYear, setSelectedYear] = useState(initialForecastYear);
  const [selectedProductCategory, setSelectedProductCategory] =
    useState<Enums.ProductCategory | null>(null);
  const [isRedistributeDialogOpen, setIsRedistributeDialogOpen] = useState(false);
  const manuallyEditedRef = useRef<Record<string, Record<number, boolean>>>({});
  const initializedCategoriesRef = useRef<Set<string>>(new Set());
  // Keep a ref to latest forecasts, synced via useLayoutEffect so it's always
  // current before any passive useEffect runs in the same render cycle.
  // This lets Effect 6 read the latest forecasts without declaring
  // formikBag.values.forecasts as a dep (which would cause infinite re-trigger loops).
  const forecastsRef = useRef(formikBag.values.forecasts);
  useLayoutEffect(() => {
    forecastsRef.current = formikBag.values.forecasts;
  });

  const availableCategories = useMemo(() => {
    return getProductCategoriesInProject(formikBag.values.productsByPlants);
  }, [formikBag.values.productsByPlants]);

  /**
   * True when the project has no products whose category participates in
   * forecasts (Mix/Aggregate/Asphalt). In this state the month inputs are
   * disabled and an empty-state message is shown.
   */
  const hasNoForecastableProducts = availableCategories.length === 0;

  const buildForecastsForCategory = useCallback(
    (
      category: Enums.ProductCategory,
      baseForecasts: ProjectFormikType['forecasts'],
    ): ProjectFormikType['forecasts'] => {
      const categoryWithForecasts = availableCategories.find((cat) =>
        baseForecasts.some((f) => f.productCategory === cat),
      );

      if (categoryWithForecasts) {
        return baseForecasts
          .filter((f) => f.productCategory === categoryWithForecasts)
          .map((f) => ({
            ...f,
            productCategory: category,
            value: '',
          }));
      }

      return Forecast.fillForecasts({
        kind: Enums.ForecastKind.Project,
        currentForecasts: [],
        productCategory: category,
        tenantUnits: getTenantUnits(),
      }).map<ProjectFormikType['forecasts'][number]>((pf) => ({
        id: pf.id,
        kind: pf.kind,
        value: '',
        unit: pf.unit,
        productCategory: pf.productCategory,
        intervalLength: pf.intervalLength.toISO() ?? '',
        intervalStart: pf.intervalStart.toISO() ?? '',
      }));
    },
    [availableCategories],
  );

  const getCategoryTracker = useCallback((category: Enums.ProductCategory | null) => {
    if (!category) return {};
    const categoryKey = String(category);
    if (!manuallyEditedRef.current[categoryKey]) {
      manuallyEditedRef.current[categoryKey] = {};
    }
    return manuallyEditedRef.current[categoryKey];
  }, []);

  useEffect(() => {
    const estStartDate = formikBag.values.estimatedStartDate;
    if (hasStringValue(estStartDate)) {
      const parsedDate = ParseServerDate(estStartDate);
      if (parsedDate !== null && typeof parsedDate.year === 'number') {
        setSelectedYear(parsedDate.year);
        return;
      }
    }
    setSelectedYear(initialForecastYear);
  }, [formikBag.values.estimatedStartDate, initialForecastYear]);

  useEffect(() => {
    if (availableCategories.length > 0 && !selectedProductCategory) {
      setSelectedProductCategory(availableCategories[0]);
    }
    if (selectedProductCategory && !availableCategories.includes(selectedProductCategory)) {
      setSelectedProductCategory(availableCategories[0] || null);
    }
  }, [availableCategories, selectedProductCategory]);

  useEffect(() => {
    if (!selectedProductCategory) {
      return;
    }

    const hasForecastsForCategory = formikBag.values.forecasts.some(
      (f) => f.productCategory === selectedProductCategory,
    );

    if (hasForecastsForCategory) {
      return;
    }

    const newForecasts = buildForecastsForCategory(
      selectedProductCategory,
      formikBag.values.forecasts,
    );

    if (newForecasts.length > 0) {
      let nextForecasts = [...formikBag.values.forecasts, ...newForecasts];
      const categoryTotal = sumProductQuantitiesByCategory(
        formikBag.values.productsByPlants,
        selectedProductCategory,
      );

      if (categoryTotal > 0) {
        const categoryForecastsExtra = nextForecasts
          .map((f, i) => {
            const { year, month } = ParseServerDate(f.intervalStart);
            return { ...f, formikIdx: i, year, month };
          })
          .filter((f) => f.productCategory === selectedProductCategory);

        if (categoryForecastsExtra.length > 0) {
          const result = distributeForecastVolume(
            {
              ...formikBag.values,
              forecasts: nextForecasts,
              readyMixQuantityOverride: categoryTotal,
            },
            categoryForecastsExtra,
            {
              distributionMethod: hasStringValue(formikBag.values.forecastDistributionMethod)
                ? formikBag.values.forecastDistributionMethod
                : 'monthly',
              respectManualEdits: false,
              clearOutsideDuration: false,
            },
          );

          nextForecasts = result.forecasts;
          initializedCategoriesRef.current.add(String(selectedProductCategory));
        }
      }

      formikBag.setFieldValue('forecasts', nextForecasts);
    }
  }, [
    buildForecastsForCategory,
    formikBag.setFieldValue,
    formikBag.values.forecasts,
    formikBag.values.forecastDistributionMethod,
    formikBag.values.productsByPlants,
    selectedProductCategory,
  ]);

  // Distribute values for existing categories that have entries but no
  // distributed values yet (e.g. when the form first mounts on the Forecasts
  // step and entries were already created by useForecastSync).
  // Entry creation and cleanup for added/removed categories is handled by
  // useForecastSync in ProjectDrawerSections (always mounted).
  useEffect(() => {
    if (availableCategories.length === 0) {
      return;
    }

    let needsUpdate = false;
    let updatedForecasts = [...forecastsRef.current];

    const initStartDate = formikBag.values.estimatedStartDate;
    const initEndDate = formikBag.values.estimatedEndDate;
    const datesAlreadySet =
      isNonEmptyString(initStartDate) &&
      isNonEmptyString(initEndDate) &&
      DateTime.fromISO(initStartDate).isValid &&
      DateTime.fromISO(initEndDate).isValid;

    availableCategories.forEach((category) => {
      const categoryKey = String(category);
      const hasForecasts = updatedForecasts.some((f) => f.productCategory === category);

      if (!hasForecasts) {
        return; // useForecastSync handles entry creation
      }

      const categoryTotal = sumProductQuantitiesByCategory(
        formikBag.values.productsByPlants,
        category,
      );

      const hasDistributedValues = updatedForecasts.some(
        (f) => f.productCategory === category && f.value && f.value !== '' && f.value !== '0',
      );

      const shouldDistribute = categoryTotal > 0 && !hasDistributedValues;

      if (initializedCategoriesRef.current.has(categoryKey) && !shouldDistribute) {
        return;
      }

      if (shouldDistribute) {
        const categoryForecastsExtra = updatedForecasts
          .map((f, i) => {
            const { year, month } = ParseServerDate(f.intervalStart);
            return { ...f, formikIdx: i, year, month };
          })
          .filter((f) => f.productCategory === category);

        if (categoryForecastsExtra.length > 0) {
          const result = distributeForecastVolume(
            {
              ...formikBag.values,
              forecasts: updatedForecasts,
              readyMixQuantityOverride: categoryTotal,
            },
            categoryForecastsExtra,
            {
              distributionMethod: hasStringValue(formikBag.values.forecastDistributionMethod)
                ? formikBag.values.forecastDistributionMethod
                : 'monthly',
              respectManualEdits: false,
              clearOutsideDuration: datesAlreadySet,
            },
          );

          updatedForecasts = result.forecasts;
          needsUpdate = true;
        }

        initializedCategoriesRef.current.add(categoryKey);
      }
    });

    if (needsUpdate) {
      formikBag.setFieldValue('forecasts', updatedForecasts);
    }
    // Note: estimatedStartDate and estimatedEndDate are intentionally omitted from deps.
    // This effect fires once per category (guarded by initializedCategoriesRef) to distribute
    // the initial volume. Re-running on date changes would re-distribute already-initialized
    // categories and overwrite manual edits.
  }, [availableCategories, buildForecastsForCategory, formikBag.values.productsByPlants]);

  const allForecastsExtra = useMemo(
    () =>
      formikBag.values.forecasts.map((f, i) => {
        const { year, month } = ParseServerDate(f.intervalStart);
        return { ...f, formikIdx: i, year, month };
      }),
    [formikBag.values.forecasts],
  );

  const forecastsExtra = useMemo(
    () => allForecastsExtra.filter((f) => f.productCategory === selectedProductCategory),
    [allForecastsExtra, selectedProductCategory],
  );

  const possibleYears = useMemo(
    () =>
      uniqBy(forecastsExtra, (f: (typeof forecastsExtra)[number]) => f.year)
        .map((yr: (typeof forecastsExtra)[number]) => ({
          year: yr.year,
          hasValues: forecastsExtra.some(
            (f: (typeof forecastsExtra)[number]) =>
              f.value !== '' && f.value !== null && f.value !== undefined && f.year === yr.year,
          ),
        }))
        .filter((entry) => Number.isFinite(entry.year)),
    [forecastsExtra],
  );
  const hasYearOptions = possibleYears.length > 0;

  useEffect(() => {
    if (possibleYears.length === 0) {
      return;
    }
    const hasSelectedYear = possibleYears.some((y) => y.year === selectedYear);
    if (!hasSelectedYear) {
      // If the current year isn't in the list, jump to the first year with actual data,
      // or fall back to the first available year.
      const firstWithData = possibleYears.find((y) => y.hasValues);
      setSelectedYear(firstWithData ? firstWithData.year : possibleYears[0].year);
    }
  }, [possibleYears, selectedYear]);

  const startDate = formikBag.values.estimatedStartDate;
  const endDate = formikBag.values.estimatedEndDate;
  const hasBothDates = hasStringValue(startDate) && hasStringValue(endDate);

  const sumOfProductQuantities = useMemo(() => {
    if (!selectedProductCategory) return 0;
    return sumProductQuantitiesByCategory(
      formikBag.values.productsByPlants,
      selectedProductCategory,
    );
  }, [formikBag.values.productsByPlants, selectedProductCategory]);

  const getCategoryOverride = useCallback(
    (category: Enums.ProductCategory): number | null | undefined => {
      switch (category) {
        case Enums.ProductCategory.ReadyMix:
          return formikBag.values.readyMixQuantityOverride;
        case Enums.ProductCategory.Aggregate:
          return formikBag.values.aggregateQuantityOverride;
        case Enums.ProductCategory.Asphalt:
          return formikBag.values.asphaltQuantityOverride;
        default:
          return null;
      }
    },
    [
      formikBag.values.readyMixQuantityOverride,
      formikBag.values.aggregateQuantityOverride,
      formikBag.values.asphaltQuantityOverride,
    ],
  );

  const sourceTotalVolume = useMemo(() => {
    if (!selectedProductCategory) return sumOfProductQuantities;
    const rawEstVol = getCategoryOverride(selectedProductCategory);

    if (rawEstVol !== null && rawEstVol !== undefined) {
      if (typeof rawEstVol === 'number' && !Number.isNaN(rawEstVol)) {
        return rawEstVol;
      }
    }

    return sumOfProductQuantities;
  }, [getCategoryOverride, selectedProductCategory, sumOfProductQuantities]);

  const displayedEstVolume = useMemo(() => {
    const rawEstVol = selectedProductCategory ? getCategoryOverride(selectedProductCategory) : null;

    if (rawEstVol !== null && rawEstVol !== undefined) {
      if (typeof rawEstVol === 'number' && !Number.isNaN(rawEstVol)) {
        return String(rawEstVol);
      }
    }

    return String(sumOfProductQuantities);
  }, [getCategoryOverride, selectedProductCategory, sumOfProductQuantities]);

  const volumeFieldLabel = useMemo(() => {
    const rawEstVol = selectedProductCategory ? getCategoryOverride(selectedProductCategory) : null;
    const categoryName = selectedProductCategory
      ? getUserFacingCategoryName(selectedProductCategory)
      : '';
    const prefix = categoryName ? `${categoryName} ` : '';

    if (rawEstVol !== null && rawEstVol !== undefined) {
      if (typeof rawEstVol === 'number' && !Number.isNaN(rawEstVol)) {
        return `${prefix}${EST_VOLUME_OVERRIDE_LABEL}`;
      }
    }

    return `${prefix}${TOTAL_QUANTITY_LABEL}`;
  }, [getCategoryOverride, selectedProductCategory]);

  // Build a 12-month array for the selected year, filling in API values where present
  const allMonthsForYear = useMemo(() => {
    return Array.from({ length: 12 }, (_, i) => {
      const month = i + 1;
      const formikIdx = formikBag.values.forecasts.findIndex((f) => {
        const dt = ParseServerDate(f.intervalStart);
        return (
          dt !== null &&
          dt.year === selectedYear &&
          dt.month === month &&
          f.productCategory === selectedProductCategory
        );
      });
      if (formikIdx !== -1) {
        const f = formikBag.values.forecasts[formikIdx];
        return {
          ...f,
          formikIdx,
          year: selectedYear,
          month,
        };
      }
      const dtObject = DateTime.fromObject({ year: selectedYear, month });
      const isoString = dtObject.isValid ? dtObject.toISO() : null;
      return {
        formikIdx: -1,
        intervalStart: isoString !== null ? isoString : '',
        value: '',
        year: selectedYear,
        month,
      };
    });
  }, [formikBag.values.forecasts, selectedYear, selectedProductCategory]);

  const isForecastInProjectDuration = (f: { year: number; month: number }): boolean => {
    if (!isNonEmptyString(startDate) || !isNonEmptyString(endDate)) {
      return true;
    }

    const startDt = DateTime.fromISO(startDate);
    const endDt = DateTime.fromISO(endDate);

    if (!startDt.isValid || !endDt.isValid) {
      return false;
    }
    return (
      (f.year > startDt.year || (f.year === startDt.year && f.month >= startDt.month)) &&
      (f.year < endDt.year || (f.year === endDt.year && f.month <= endDt.month))
    );
  };

  // Calculate cumulative total within the project duration (start to end date)
  const distributedSum = useMemo(() => {
    if (!isNonEmptyString(startDate) || !isNonEmptyString(endDate)) {
      return 0;
    }

    const startDt = DateTime.fromISO(startDate);
    const endDt = DateTime.fromISO(endDate);

    if (!startDt.isValid || !endDt.isValid) {
      return 0;
    }

    const filtered = forecastsExtra.filter((f) => isForecastInProjectDuration(f));

    return filtered.reduce((sum, f) => {
      const val = parseFloat(f.value);
      return sum + (Number.isNaN(val) ? 0 : val);
    }, 0);
  }, [forecastsExtra, startDate, endDate]);

  const handleRedistribute = useCallback(
    (useOriginalVolume: boolean) => {
      if (!selectedProductCategory) return;

      // Clear all manual edits for this category in the selected year
      const categoryKey = String(selectedProductCategory);
      if (!manuallyEditedRef.current[categoryKey]) {
        manuallyEditedRef.current[categoryKey] = {};
      }

      const volumeToDistribute = useOriginalVolume ? sourceTotalVolume : distributedSum;

      if (volumeToDistribute <= 0) {
        return;
      }

      // Get all forecasts for the selected year and category
      const allForecastsForSelectedYearAndCategory = allForecastsExtra.filter(
        (f) => f.year === selectedYear && f.productCategory === selectedProductCategory,
      );

      // Filter to only include forecasts within the project duration (start to end date)
      const forecastsForSelectedYear = allForecastsForSelectedYearAndCategory.filter((f) =>
        isForecastInProjectDuration(f),
      );

      if (forecastsForSelectedYear.length === 0) {
        return;
      }

      // Clear forecasts that are outside the project duration
      allForecastsForSelectedYearAndCategory.forEach((forecast) => {
        if (!isForecastInProjectDuration(forecast)) {
          formikBag.setFieldValue(`forecasts.${forecast.formikIdx}.value`, '');
          delete manuallyEditedRef.current[categoryKey][forecast.formikIdx];
        }
      });

      const distributionMethod = hasStringValue(formikBag.values.forecastDistributionMethod)
        ? formikBag.values.forecastDistributionMethod
        : 'monthly';

      if (distributionMethod === 'monthly') {
        const numMonths = forecastsForSelectedYear.length;
        const basePerMonth = Math.floor(volumeToDistribute / numMonths);
        const remainder = volumeToDistribute % numMonths;

        forecastsForSelectedYear.forEach((forecast, idx) => {
          const value = basePerMonth + (idx < remainder ? 1 : 0);
          formikBag.setFieldValue(`forecasts.${forecast.formikIdx}.value`, String(value));
          delete manuallyEditedRef.current[categoryKey][forecast.formikIdx];
        });
      } else {
        const quarterMap: { [key: string]: typeof forecastsForSelectedYear } = {};

        forecastsForSelectedYear.forEach((forecast) => {
          const quarterIndex = Math.floor((forecast.month - 1) / 3);
          const quarterKey = `Q${quarterIndex}`;
          if (!quarterMap[quarterKey]) {
            quarterMap[quarterKey] = [];
          }
          quarterMap[quarterKey].push(forecast);
        });

        const quarters = Object.values(quarterMap);
        const numQuarters = quarters.length;
        const basePerQuarter = Math.floor(volumeToDistribute / numQuarters);
        const remainderQuarters = volumeToDistribute % numQuarters;

        quarters.forEach((quarterForecasts, qIdx) => {
          const quarterVolume = basePerQuarter + (qIdx < remainderQuarters ? 1 : 0);
          const numMonthsInQuarter = quarterForecasts.length;
          const basePerMonth = Math.floor(quarterVolume / numMonthsInQuarter);
          const remainderMonths = quarterVolume % numMonthsInQuarter;

          quarterForecasts.forEach((forecast, mIdx) => {
            const value = basePerMonth + (mIdx < remainderMonths ? 1 : 0);
            formikBag.setFieldValue(`forecasts.${forecast.formikIdx}.value`, String(value));
            delete manuallyEditedRef.current[categoryKey][forecast.formikIdx];
          });
        });
      }
    },
    [
      selectedProductCategory,
      selectedYear,
      sourceTotalVolume,
      distributedSum,
      formikBag,
      allForecastsExtra,
      isForecastInProjectDuration,
    ],
  );

  const manualHistoryRef = useRef<Set<number>>(new Set());
  const prevMethod = usePreviousValue(formikBag.values.forecastDistributionMethod);
  const handleManualEdit = useCallback((formikIndex: number) => {
    manualHistoryRef.current.add(formikIndex);
  }, []);

  // Date-change redistribution is handled in ProjectDrawer.tsx.
  // Product-quantity / override redistribution for ALL categories is handled
  // by useForecastSync (always mounted in ProjectDrawerSections).

  useEffect(() => {
    const methodChanged =
      hasValue(prevMethod) && prevMethod !== formikBag.values.forecastDistributionMethod;

    if (methodChanged) {
      // Switching between monthly and quarterly is just a view change
      // Do NOT redistribute - the data should remain the same
      manualHistoryRef.current.clear();
    }
  }, [formikBag.values.forecastDistributionMethod, prevMethod]);

  // Calculate total distributed across ALL years (for display and for calculating remaining)
  const distributedSumAllYears = useMemo(() => {
    return forecastsExtra.reduce((sum, f) => {
      const val = parseFloat(f.value);
      return sum + (Number.isNaN(val) ? 0 : val);
    }, 0);
  }, [forecastsExtra]);

  // Calculate remaining budget (total volume minus what's distributed across all years)
  const remaining = useMemo(() => {
    return sourceTotalVolume - distributedSumAllYears;
  }, [sourceTotalVolume, distributedSumAllYears]);

  const quarterLabels = ['1st', '2nd', '3rd', '4th'];

  const theme = useTheme();

  const productCategoryUnitLabel = useMemo(() => {
    if (!selectedProductCategory) {
      return getUnitLabel(getTenantUnits().dryVolume);
    }
    return getUnitLabel(getDefaultUnitForCategory(selectedProductCategory, getTenantUnits()));
  }, [selectedProductCategory]);

  return (
    <Box display='flex' flexDirection='column' gap='1rem'>
      {availableCategories.length > 1 && (
        <Box
          sx={{
            width: '100%',
            borderBottom: `1px solid ${theme.palette.SlabGray.Stroke}`,
          }}
        >
          <SlabTabs
            value={selectedProductCategory ?? availableCategories[0]}
            onChange={setSelectedProductCategory}
            items={availableCategories.map((category) => {
              const categoryTotal = sumProductQuantitiesByCategory(
                formikBag.values.productsByPlants,
                category,
              );
              const unitLabel = getShortUnitLabel(
                getDefaultUnitForCategory(category, getTenantUnits()),
              );
              const distributedForCategory = formikBag.values.forecasts.reduce((sum, f) => {
                if (f.productCategory !== category) return sum;
                const val = parseFloat(f.value);
                return sum + (Number.isNaN(val) ? 0 : val);
              }, 0);

              return {
                value: category,
                label: getUserFacingCategoryName(category),
                count: Math.round(distributedForCategory),
                maxCount: Infinity,
                subtitle: `${Math.round(categoryTotal).toLocaleString('en-US')} ${unitLabel}`,
              };
            })}
          />
        </Box>
      )}

      <Box sx={{ width: '100%', mb: 2 }}>
        <Grid container spacing={2} alignItems='flex-end' sx={{ mb: 2 }}>
          <Grid item xs={4}>
            <Box
              onClick={() => {
                if (volumeFieldLabel.includes(EST_VOLUME_OVERRIDE_LABEL)) {
                  onNavigateSection?.(ProjectSection.DETAILS);
                } else if (volumeFieldLabel.includes(TOTAL_QUANTITY_LABEL)) {
                  onNavigateSection?.(ProjectSection.PRODUCT_INFO);
                }
              }}
              sx={{
                cursor:
                  volumeFieldLabel.includes(EST_VOLUME_OVERRIDE_LABEL) ||
                  volumeFieldLabel.includes(TOTAL_QUANTITY_LABEL)
                    ? 'pointer'
                    : 'default',
                width: '100%',
              }}
            >
              <TextField
                type='number'
                label={volumeFieldLabel}
                value={displayedEstVolume}
                placeholder='Enter total volume'
                fullWidth
                disabled
                sx={{ pointerEvents: 'none' }}
              />
            </Box>
          </Grid>
          <Grid item xs={3}>
            <YearSelector
              selectedYear={selectedYear}
              setSelectedYear={setSelectedYear}
              yearOpts={hasYearOptions ? possibleYears : [{ year: selectedYear, hasValues: false }]}
              selectWidth='fullWidth'
              disabled={!hasYearOptions}
            />
          </Grid>
        </Grid>
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <PillSummary
              label='Total Distributed'
              value={distributedSumAllYears}
              unit={productCategoryUnitLabel}
            />
          </Grid>
          <Grid item xs={6}>
            <PillSummary
              label='Forecast vs Quote'
              value={remaining}
              unit={productCategoryUnitLabel}
            />
          </Grid>
        </Grid>
      </Box>

      <Box sx={{ width: '100%' }}>
        <SegmentedControl
          value={formikBag.values.forecastDistributionMethod}
          items={[
            { id: 'monthly', label: 'Monthly' },
            { id: 'quarterly', label: 'Quarterly' },
          ]}
          onChange={(value) => formikBag.setFieldValue('forecastDistributionMethod', value)}
          disabled={!hasBothDates}
          sx={{
            p: 1,
            gap: 2,
            backgroundColor: theme.palette.SlabGray[0],
            [`& .${buttonBaseClasses.root}`]: { py: 1, px: 1.25, fontSize: 18, lineHeight: '24px' },
          }}
        />
        {!hasBothDates && (
          <Typography variant='caption' sx={{ color: 'text.secondary', mt: 0.5, display: 'block' }}>
            Set project start and end dates to enable auto-distribution
          </Typography>
        )}
      </Box>

      {hasBothDates && (
        <Button
          variant='slab-action-plain'
          startIcon={<Refresh variant='TwoTone' />}
          sx={{ alignSelf: 'flex-start' }}
          disabled={!selectedProductCategory || sourceTotalVolume <= 0}
          onClick={() => setIsRedistributeDialogOpen(true)}
        >
          Redistribute Evenly
        </Button>
      )}

      {hasNoForecastableProducts && (
        <Banner
          severity='warning'
          sx={{ mb: 2 }}
        >{`Add at least one ${forecastableCategoryDisplayList()} product in the Products section to enable monthly forecasts.`}</Banner>
      )}

      <Box>
        {formikBag.values.forecastDistributionMethod === 'monthly' ? (
          <Grid key='forecast-container' container spacing={2}>
            {allMonthsForYear.map((ff) => (
              <ForecastInputRow
                key={ff.month}
                formikIdx={ff.formikIdx}
                intervalStart={ff.intervalStart}
                manuallyEditedTracker={getCategoryTracker(selectedProductCategory)}
                formikBag={formikBag}
                disableGridWrapper={false}
                isOutOfRange={!isForecastInProjectDuration(ff)}
                disabled={hasNoForecastableProducts}
                onManualEdit={handleManualEdit}
              />
            ))}
          </Grid>
        ) : (
          <Box display='flex' flexDirection='column' gap={2}>
            {[0, 1, 2, 3].map((qIdx) => {
              const quarterMonths = allMonthsForYear.slice(qIdx * 3, qIdx * 3 + 3);
              const quarterSum = quarterMonths.reduce((sum, ff) => {
                const currentValue = ff.value;
                let quarterNumericValue = 0;

                if (typeof currentValue === 'number') {
                  if (!Number.isNaN(currentValue)) {
                    quarterNumericValue = currentValue;
                  }
                } else if (typeof currentValue === 'string') {
                  if (currentValue.trim() !== '') {
                    const parsed = parseFloat(currentValue);
                    if (!Number.isNaN(parsed)) {
                      quarterNumericValue = parsed;
                    }
                  }
                }
                // quarterNumericValue is now either a valid number or 0 if input was not.
                return sum + quarterNumericValue;
              }, 0);

              return (
                <Box key={`quarter-${qIdx}`} display='flex' flexDirection='column' gap={2}>
                  <Typography variant='h5' lineHeight='26px'>
                    {`${quarterLabels[qIdx]} quarter`}
                  </Typography>
                  <Grid container spacing={2}>
                    {quarterMonths.map((ff) => (
                      <Grid item key={ff.month} xs={3}>
                        <ForecastInputRow
                          formikIdx={ff.formikIdx}
                          intervalStart={ff.intervalStart}
                          manuallyEditedTracker={getCategoryTracker(selectedProductCategory)}
                          formikBag={formikBag}
                          disableGridWrapper
                          isOutOfRange={!isForecastInProjectDuration(ff)}
                          disabled={hasNoForecastableProducts}
                          onManualEdit={handleManualEdit}
                        />
                      </Grid>
                    ))}

                    <Grid item xs={3}>
                      <PillSummary
                        label='Total Distributed'
                        value={quarterSum}
                        unit={productCategoryUnitLabel}
                      />
                    </Grid>
                  </Grid>
                </Box>
              );
            })}
            <Grid container spacing={2}>
              <Grid item xs={3}>
                <PillSummary label='Remaining' value={remaining} unit={productCategoryUnitLabel} />
              </Grid>
            </Grid>
          </Box>
        )}
      </Box>

      {/* Redistribute Dialog */}
      <RedistributeDialog
        isOpen={isRedistributeDialogOpen}
        originalVolume={sourceTotalVolume}
        currentDistributed={distributedSum}
        unit={productCategoryUnitLabel}
        onClose={() => setIsRedistributeDialogOpen(false)}
        onRedistribute={handleRedistribute}
      />
    </Box>
  );
};
